import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FetchError } from '@amzn/handshake-painter-typescript-client/v1';
import { useTheme } from '@amzn/meridian/theme';
import { useBundle } from '@amzn/react-arb-tools';
import { rio, rioDark } from '@amzn/rio-design-tokens';
import { ThemeProvider } from '@emotion/react';
import { useEffect, useState } from 'react';
import { matchPath, Outlet, useLocation, useParams, useSearchParams } from 'react-router';
import Column from '@amzn/meridian/column';
import { AlertProvider } from './common/alert';
import { AppError } from './common/errors';
import { Footer } from './common/footer';
import { Header } from './common/header';
import { BouncingBox } from './common/loaders';
import { queryClient } from './api/queryClient';
import { useAnalyticsContext } from './common/metrics';
import { useAppInit, useContent, useDealsStorefront, useProductComplianceHighPriority, useProductDetails, useRegularStorefront, } from './hooks';
import { useOnAppInitSuccess } from './hooks/useOnAppInitSuccess';
import { resolveStorefront } from './pages/storefrontPage/utils';
import { useBagStore } from './store/bagStore';
import { ParzivalGatewayAuthErrorType } from './utils/auth';
import { getExperimentArm } from './utils/experiments';
import { getMarketplace, isEUMarketplace } from './utils/marketplace';
import { redirectToLegacyPainter } from './utils/redirectUtils';
import { getRouteData, routes } from './utils/routes';
export const App = () => {
    var _a, _b, _c;
    /**
     * The ThemeProvider from @emotion/react allows us to pass in the rio tokens and then access
     * them in the @emotion/styled components. The "mode" from "useTheme" is set in index.tsx
     */
    const { mode } = useTheme();
    const theme = mode === 'light' ? rio : rioDark;
    const location = useLocation();
    const { id, asin } = useParams();
    const [searchParams] = useSearchParams();
    const rumClient = useAnalyticsContext();
    /**
     * DO NOT CALL useAppInit in any other file within this components. If useAppInit fails and is called
     * downstream from here, then it will try to fetch again, causing an infinite loop.
     */
    const appData = useAppInit();
    const { isError, isLoading, error, data, isSuccess } = appData;
    const onAppInitSuccess = useOnAppInitSuccess();
    const { listBag } = useBagStore();
    const bagContents = listBag();
    /**
     * 1. For EXPIRED_AUTH error on init, stay in 2.0
     * 2. On any other error (networking, MISSING_AUTH, etc), redirect to 1.0
     */
    const errorCases = [ParzivalGatewayAuthErrorType.EXPIRED_AUTH, ParzivalGatewayAuthErrorType.MISSING_AUTH];
    const redirectOnError = error instanceof FetchError ? !errorCases.includes(error.cause.errorType) : isError;
    // TODO: Follow this pattern for all pages
    // Preload the route data
    const { route, bundleId } = getRouteData(location.pathname);
    const pdpData = useProductDetails({ enabled: route.isPDP && isSuccess });
    const creatorData = useContent({ enabled: route.isCSF && isSuccess });
    const marketplaceId = getMarketplace(searchParams);
    const euLaunchArm = getExperimentArm(data === null || data === void 0 ? void 0 : data.experiments, 'WEBLAB_PAINTER_EU_LAUNCH');
    const complianceEnabled = route.isPDP && isSuccess && euLaunchArm === 'T1' && isEUMarketplace(marketplaceId);
    const complianceData = useProductComplianceHighPriority({ asin, enabled: complianceEnabled });
    // Log unknown storefront IDs on mount. Handled here rather than in the hooks since both
    // hooks are always called (React hooks can't be conditional), which would fire the event twice.
    const storefront = resolveStorefront(id);
    useEffect(() => {
        if (route.isSFP && !storefront.isValid) {
            rumClient.recordEvent('UNKNOWN_STOREFRONT_ID', { requestedId: id, url: window.location.href });
        }
    }, [id, route.isSFP, storefront.isValid]);
    const sfpEnabled = route.isSFP && isSuccess;
    const dealsStorefrontData = useDealsStorefront({
        enabled: sfpEnabled && storefront.meta.type === 'DEALS',
        storefront,
    });
    const regularStorefrontData = useRegularStorefront({
        enabled: sfpEnabled && storefront.meta.type !== 'DEALS',
        storefront,
    });
    /**
     * When the route changes, bundleId updates immediately but useBundle still returns the previous bundle
     * for one render cycle (until its internal effect re-fires). We track the previous bundleId so we can
     * detect this mismatch and show the loading state instead of rendering with stale/wrong translations.
     */
    const [bundle, isBundleLoading] = useBundle(bundleId);
    const [commonBundle, isCommonBundleLoading] = useBundle('pages.app');
    const [prevBundleId, setPrevBundleId] = useState(bundleId);
    const isBundleTransitioning = prevBundleId !== bundleId;
    useEffect(() => {
        setPrevBundleId(bundleId);
    }, [bundleId]);
    const isPageLoading = isLoading ||
        pdpData.isLoading ||
        complianceData.isLoading ||
        creatorData.isLoading ||
        dealsStorefrontData.isLoading ||
        regularStorefrontData.isLoading ||
        isBundleLoading ||
        isCommonBundleLoading ||
        isBundleTransitioning;
    // Determine if the current product is Prime eligible based on the route
    const isPrimeFromPDP = route.isPDP && ((_b = (_a = pdpData === null || pdpData === void 0 ? void 0 : pdpData.data) === null || _a === void 0 ? void 0 : _a.prime) === null || _b === void 0 ? void 0 : _b.isPrime);
    const isPrimeFromBag = (route.isPOP || route.isTYP || route.isPSP) && ((_c = bagContents[0]) === null || _c === void 0 ? void 0 : _c.isPrime);
    const isPrime = isPrimeFromPDP || isPrimeFromBag || false;
    const outletContext = {
        appData,
        pdpData,
        complianceData,
        creatorData,
        dealsStorefrontData,
        regularStorefrontData,
        storefront,
        bundle,
        commonBundle,
    };
    /**
     * Clear the product details queries on location change to ensure that we are fetching the latest data.
     * This is tied to selecting product variations and usePrefetchProductDetails
     */
    useEffect(() => {
        if (!matchPath(routes.productDetailPage, location.pathname)) {
            queryClient.removeQueries({ predicate: (query) => query.queryKey.includes('getProductDetails') });
            queryClient.removeQueries({
                predicate: (query) => query.queryKey.includes('getProductComplianceHighPriority'),
            });
        }
    }, [location.pathname]);
    // Reset scroll on page transitions
    useEffect(() => {
        var _a;
        if (!((_a = location.state) === null || _a === void 0 ? void 0 : _a.preventScrollReset)) {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [location.pathname, location.state]);
    // AppInit success case
    useEffect(() => {
        if (isSuccess) {
            onAppInitSuccess(data);
        }
        // We perform this in a useEffect so that the BoucingBox continues to render while this logic is executing
        if (redirectOnError) {
            redirectToLegacyPainter('app_init_error');
        }
    }, [redirectOnError, isSuccess]);
    if (isPageLoading || redirectOnError) {
        return _jsx(BouncingBox, {});
    }
    return (_jsxs(ThemeProvider, { theme: { mode, rio: theme }, children: [isError && (_jsx(Column, { "data-testid": "app-error", minHeight: "100vh", spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: _jsx(AppError, { error: error }) })), !isError && (_jsxs(Column, { "data-testid": "app", minHeight: "100vh", heights: ['fit', 'fill', 'fit'], spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: [_jsx(Header, { isPrime: isPrime, logosLabels: {
                            amazonLogo: commonBundle.getMessage('amazonLogo'),
                            primeLogo: commonBundle.getMessage('amazonPrimeLogo'),
                        } }), _jsx(AlertProvider, { children: _jsx(Outlet, { context: outletContext }) }, location.pathname), _jsx(Footer, { experiments: data === null || data === void 0 ? void 0 : data.experiments })] }))] }));
};
